import React, { lazy, Suspense, useEffect, useState } from "react";
import Box from '@mui/material/Box';
import {
  BrowserRouter,
  Routes,
  Route
} from "react-router-dom";
import { Avatar, Grid, CssBaseline, Container, Toolbar, Typography, Chip } from "@mui/material";
import "../styles.css";
import ResponsiveAppBar from "./Header.js";
import ErrorBoundaries from "./error-boundaries.jsx";
import { findUser, addUser, get_app, get_account ,delUser} from "./api/Functions.js";
import { useDispatch, useSelector } from "react-redux";
import actions from "./actions.js";
import Cookies from 'js-cookie';
import Ticker, { NewsTicker } from 'nice-react-ticker';
import { useLocation, useNavigate } from "react-router-dom";
import Paper from '@mui/material/Paper';
import MetaeroLoader from "./MetaeroLoader.js";
import { BrowserProvider, Contract, ZeroAddress, isAddress, formatUnits } from "ethers"; // v6

const Dashboard = lazy(() => import("./Dashboard.js"));
const Profile = lazy(() => import("./Profile.jsx"));
const Billing = lazy(() => import("./Billing.jsx"));
const Confirmation = lazy(() => import("./confirmation.jsx"));
const FlightSearch = lazy(() => import("./search/flight-search.jsx"));
const MyAlerts = lazy(() => import("./my_alerts/my_alerts.jsx"));
const ActivityLog = lazy(() => import("./activity_log/activity_log.jsx"));
const About = lazy(() => import("./About.js"));
const Terms = lazy(() => import("./Terms.jsx"));
const Privacy = lazy(() => import("./Privacy.jsx"));
const Pricing = lazy(() => import("./Pricing.jsx"));
const Connect = lazy(() => import("./Connect.js"));
const FlightBooking = lazy(() => import("./booking/flight-booking.jsx"));

/* ---------- Pilot NFT banner component ---------- */
const PILOT_NFT_ADDRESS =
  (process.env.REACT_APP_PILOT_NFT_ADDRESS || "0x0000000000000000000000000000000000000000").trim();
const FLY_TOKEN_ADDRESS =
  (process.env.REACT_APP_FLY_TOKEN_ADDRESS || "0x0000000000000000000000000000000000000000").trim();
const METAR_TOKEN_ADDRESS =
  (process.env.REACT_APP_METAR_TOKEN_ADDRESS || "0x0000000000000000000000000000000000000000").trim();

const EXPECT_CHAIN_ID_HEX = (process.env.REACT_APP_CHAIN_ID_HEX || "0x1").trim(); // mainnet by default
const DEBUG = String(process.env.REACT_APP_DEBUG_PILOT || "") === "1";

const PILOT_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function xboxIdOf(address p) view returns (uint256)",
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)"
];

// minimal ERC20 ABI
const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)"
];

function ipfsToHttp(u) {
  if (!u) return "";
  if (u.startsWith("ipfs://")) {
    return "https://ipfs.io/ipfs/" + u.replace("ipfs://", "");
  }
  return u;
}

function PilotBanner({ walletAddress }) {
  const [state, setState] = useState({
    loading: true,
    hasNFT: false,
    tokenId: null,
    meta: null,
    name: "",
    symbol: "",
    xboxId: 0,
    flyBalance: "",
    flySymbol: "FLY",
    metarBalance: "",
    metarSymbol: "METAR",
    debug: ""
  });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const setDbg = (msg) => {
        if (DEBUG && !cancelled) setState((s) => ({ ...s, debug: msg }));
      };

      try {
        if (!window.ethereum) {
          setDbg("no window.ethereum");
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
          return;
        }
        if (!isAddress(PILOT_NFT_ADDRESS)) {
          setDbg("bad contract address (PILOT)");
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
          return;
        }

        const provider = new BrowserProvider(window.ethereum);

        // Optional: check chain, but do NOT prompt
        try {
          const chainId = await provider.send("eth_chainId", []);
          if (chainId !== EXPECT_CHAIN_ID_HEX) {
            setDbg(`wrong chain ${chainId}, need ${EXPECT_CHAIN_ID_HEX}`);
          }
        } catch {
          // ignore
        }

        // Ensure contract code exists at address on this chain
        const codePilot = await provider.getCode(PILOT_NFT_ADDRESS);
        if (!codePilot || codePilot === "0x") {
          setDbg("no code at PILOT address (wrong network?)");
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
          return;
        }

        // ---- Resolve address (SILENT ONLY) ----
        let addr = walletAddress;
        if (!addr) {
          const accts = await provider.send("eth_accounts", []);
          addr = accts?.[0] || null;
        }
        if (!addr) {
          // user is disconnected — exit quietly without forcing connect
          setDbg("wallet not connected (silent)");
          if (!cancelled) {
            setState({
              loading: false,
              hasNFT: false,
              tokenId: null,
              meta: null,
              name: "",
              symbol: "",
              xboxId: 0,
              flyBalance: "",
              flySymbol: "FLY",
              metarBalance: "",
              metarSymbol: "METAR",
              debug: DEBUG ? "wallet not connected" : ""
            });
          }
          return;
        }
        // --------------------------------------

        const contract = new Contract(PILOT_NFT_ADDRESS, PILOT_ABI, provider);

        // Balance
        let bal;
        try {
          bal = await contract.balanceOf(addr);
        } catch (e) {
          setDbg("balanceOf failed (abi/network?)");
          if (!cancelled) setState((s) => ({ ...s, loading: false }));
          return;
        }

        if (bal === 0n) {
          setDbg("balance=0");
          if (!cancelled) {
            setState({
              loading: false,
              hasNFT: false,
              tokenId: null,
              meta: null,
              name: "",
              symbol: "",
              xboxId: 0,
              flyBalance: "",
              flySymbol: "FLY",
              metarBalance: "",
              metarSymbol: "METAR",
              debug: DEBUG ? "no NFT" : ""
            });
          }
          return;
        }

        // Discover a tokenId
        let tokenId = null;
        try {
          const idx = bal - 1n;
          tokenId = await contract.tokenOfOwnerByIndex(addr, idx);
          setDbg(`found by enumerable idx=${idx} tokenId=${tokenId}`);
        } catch {
          try {
            const latest = await provider.getBlockNumber();
            const from = latest > 2_000_000 ? latest - 2_000_000 : 0n;
            const mints = await contract.queryFilter(contract.filters.Transfer(ZeroAddress, addr), from, "latest");
            const last = mints[mints.length - 1];
            tokenId = last?.args?.tokenId ?? null;

            if (tokenId == null) {
              const anyIn = await contract.queryFilter(contract.filters.Transfer(null, addr), from, "latest");
              tokenId = anyIn[anyIn.length - 1]?.args?.tokenId ?? null;
              setDbg(`found by events anyIn tokenId=${String(tokenId ?? "")}`);
            } else {
              setDbg(`found by events mint tokenId=${String(tokenId)}`);
            }
          } catch (e) {
            setDbg("events lookup failed");
          }
        }

        // Metadata
        let meta = null;
        if (tokenId != null) {
          try {
            const tokenURI = await contract.tokenURI(tokenId);
            const httpURI = ipfsToHttp(tokenURI);
            try {
              const resp = await fetch(httpURI);
              meta = await resp.json();
            } catch {
              meta = { name: "Pilot NFT", image: httpURI || "", description: "" };
            }
          } catch {
            setDbg("tokenURI failed");
          }
        } else {
          setDbg("no tokenId discovered");
        }

        // Basic info
        const [name, symbol, xboxId] = await Promise.all([
          contract.name().catch(() => "Metaero Pilot"),
          contract.symbol().catch(() => "PILOT"),
          contract.xboxIdOf(addr).catch(() => 0)
        ]);

        // ----- Helper to fetch ERC20 balance safely -----
        async function fetchErc20BalanceSafe(address, tokenAddr, label) {
          try {
            if (!isAddress(tokenAddr)) return { balance: "", symbol: label };
            const code = await provider.getCode(tokenAddr);
            if (!code || code === "0x") return { balance: "", symbol: label };

            const erc = new Contract(tokenAddr, { "fragment": ERC20_ABI }["fragment"] ? ERC20_ABI : ERC20_ABI, provider);
            const [raw, dec, sym] = await Promise.all([
              erc.balanceOf(address),
              erc.decimals().catch(() => 18),
              erc.symbol().catch(() => label)
            ]);
            const asStr = formatUnits(raw, dec || 18);
            const n = Number(asStr);
            const formatted = Number.isFinite(n)
              ? n.toLocaleString(undefined, { maximumFractionDigits: 4 })
              : asStr;
            return { balance: formatted, symbol: sym || label };
          } catch {
            return { balance: "", symbol: label };
          }
        }

        // ----- FLY & METAR balances -----
        const [{ balance: flyBalanceFormatted, symbol: flySymbol },
               { balance: metarBalanceFormatted, symbol: metarSymbol }] = await Promise.all([
          fetchErc20BalanceSafe(addr, FLY_TOKEN_ADDRESS, "FLY"),
          fetchErc20BalanceSafe(addr, METAR_TOKEN_ADDRESS, "METAR")
        ]);

        if (!cancelled) {
          setState({
            loading: false,
            hasNFT: bal > 0n,
            tokenId,
            meta,
            name,
            symbol,
            xboxId: Number(xboxId || 0),
            flyBalance: flyBalanceFormatted,
            flySymbol,
            metarBalance: metarBalanceFormatted,
            metarSymbol,
            debug: DEBUG ? (meta ? "ok" : "no meta") : ""
          });
        }
      } catch (e) {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            debug: DEBUG ? `error: ${e?.message || e}` : ""
          }));
        console.error("PilotBanner error:", e);
      }
    }

    run();

    // Refresh on account/chain changes (still silent)
    const onAccounts = () => run();
    const onChain = () => run();
    window.ethereum?.on?.("accountsChanged", onAccounts);
    window.ethereum?.on?.("chainChanged", onChain);

    return () => {
      window.ethereum?.removeListener?.("accountsChanged", onAccounts);
      window.ethereum?.removeListener?.("chainChanged", onChain);
    };
  }, [walletAddress]);

  const DebugLine =
    DEBUG && state.debug ? (
      <Typography variant="caption" sx={{ display: "block", color: "#9ca3af", mb: 0.5 }}>
        [PILOT DEBUG] {state.debug}
      </Typography>
    ) : null;

  if (state.loading) return DebugLine;
  if (!state.hasNFT) return DebugLine;

  const img = ipfsToHttp(state?.meta?.image || "");
  const title = state?.meta?.name || `${state.name}${state.tokenId != null ? ` #${state.tokenId}` : ""}`;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: "2px solid #333",
        background: "linear-gradient(180deg,#0b1020,#101826)",
        color: "white"
      }}
    >
      {DebugLine}
      <Grid container spacing={2} alignItems="center">
        {img ? (
          <Grid item xs={12} sm="auto">
            <Avatar variant="rounded" src={img} alt={title} sx={{ width: 84, height: 84, border: "1px solid #444" }} />
          </Grid>
        ) : null}
        <Grid item xs>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#cbd5e1" }}>
            {state.symbol}
            {state.tokenId != null ? ` #${state.tokenId}` : ""} · Xbox ID: {state.xboxId || "—"}
          </Typography>

          {(state.flyBalance || state.metarBalance) && (
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {state.flyBalance && (
                <Chip
                  variant="outlined"
                  size="small"
                  label={`🪙 ${state.flyBalance} ${state.flySymbol}`}
                  sx={{ borderColor: "#334155", color: "#e2e8f0" }}
                />
              )}
              {state.metarBalance && (
                <Chip
                  variant="outlined"
                  size="small"
                  label={`🪙 ${state.metarBalance} ${state.metarSymbol}`}
                  sx={{ borderColor: "#334155", color: "#e2e8f0" }}
                />
              )}
            </Box>
          )}

          {state?.meta?.description && (
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              {state.meta.description}
            </Typography>
          )}
          {state?.meta?.external_url && (
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              <a href={state.meta.external_url} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>
                View more
              </a>
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

/* ---------- end Pilot NFT banner ---------- */

const Home = () => {
  const [user, setUser] = useState({});
  const [noMSFS, setNoMSFS] = useState(false); // 🚨 show banner + hide stats if true

  const userInfo = useSelector((state) => state.userInfo);
  const userAccount = useSelector((state) => state.userAccount);
  const userActivity = useSelector((state) => state.userActivity);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function handleMSFSsignup() {
    dispatch({ type: actions.SET_USER_LOADING });
  }

  // 1) Existing login/cookies effect
  useEffect(() => {
    if (params.get("code") != null) {
      get_app(params.get("code")).then(function (result) {
        if (!result.app_key) return;

        dispatch({ type: actions.SET_USER_LOADING });

        const data = {
          app_key: result.app_key,
          email: result.email,
          name: result.gamertag,
          picture: result.avatar,
          xbox_id: result.xuid
        };
        // alert("DEBUG3")
        setUser(result);
        //delUser({email:result.email})
        findUser(data).then(function (result) {
          let user_id = "";
          if (result.message === "not_found") {
            addUser(data).then(function (added) {
              dispatch({ type: actions.SET_USER_OBJECT, data: added.data });
              user_id = added.data.id;

              Cookies.set('user', JSON.stringify(added.data), { expires: 7 });
              if (!Cookies.get('user')) {
                alert("Cookies are disabled can't continue...");
                navigate("/");
              }
            });
          } else {
            try {
              if (user_id === "") user_id = result.user.id;
            } catch (error) {
              console.error("Error accessing result.user.id:", error);
              navigate("/");
            }
            if (user_id) {
              dispatch({ type: actions.SET_USER_OBJECT, data: result.user });
              Cookies.set('user', JSON.stringify(result.user), { expires: 7 });
            }
          }

          try {
          //  get_account((result.user?.app_key) ?? data.app_key).then(function (acct) {
           get_account(data.app_key).then(function (acct) {
              // ✅ Handle "no MSFS title" softly
              if (acct?.error === "msfs_title_not_found") {
                setNoMSFS(true);
                // Do NOT dispatch account info so MSFS UI stays hidden
                navigate("dashboard");
                return;
              }
              if (!acct || (typeof acct === 'object' && Object.keys(acct).length === 0)) {
                navigate("/");
                return;
              }
              dispatch({ type: actions.SET_ACCOUNT_INFO, payload: acct });
              navigate("dashboard");
            }).catch((err) => {
              if (err?.code === "msfs_title_not_found") {
                setNoMSFS(true);
                navigate("dashboard");
                return;
              }
              console.error("Error with server", err);
              navigate("/");
            });
          } catch (error) {
            console.error("Error with server", error);
            navigate("/");
          }
        });
      });
    }

    let cookieUser = Cookies.get('user');
    //TEMP NEED TO SQL UPDATE NEW APP_KEY
    cookieUser=0
    if (!cookieUser) {
      //alert("DEBUG1")
      dispatch({ type: actions.SET_ACTIVITY_LOADING_FALSE });
      navigate("/");
    } else {
      //alert("DEBUG2")
      dispatch({ type: actions.SET_ACTIVITY_LOADING_TRUE });
      if (!userInfo.result) {
        const parsed = JSON.parse(cookieUser);
        if (parsed) dispatch({ type: actions.SET_USER_OBJECT, data: parsed });

        get_account(parsed.app_key).then(function (acct) {
          // ✅ Handle "no MSFS title" softly
          if (acct?.error === "msfs_title_not_found") {
            setNoMSFS(true);
            // no dispatch — hides MSFS stats UI
            navigate("dashboard");
            return;
          }
          if (!acct || (typeof acct === 'object' && Object.keys(acct).length === 0)) {
            Cookies.remove('user');
            navigate(0);
            navigate('/');
          } else {
            dispatch({ type: actions.SET_ACCOUNT_INFO, payload: acct });
            navigate("dashboard");
          }
        }).catch((err) => {
          if (err?.code === "msfs_title_not_found") {
            setNoMSFS(true);
            navigate("dashboard");
            return;
          }
          console.error("Error with server", err);
          navigate("/");
        });
      }
      dispatch({ type: actions.SET_ACTIVITY_LOADING_FALSE });
    }
  }, []); // eslint-disable-line

  // 2) New effect: monitor MetaMask account/network and route to dashboard
  useEffect(() => {
    if (!window?.ethereum?.on) return;

    const goToDashboard = () => {
      if (location.pathname === '/dashboard') {
        navigate(0); // refresh if already on dashboard
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    const onAccountsChanged = (accounts) => {
      dispatch({
        type: actions.SET_ACCOUNT_INFO,
        payload: {
          ...userAccount,
          walletAddress: accounts?.[0] || null,
        },
      });
      goToDashboard();
    };

    const onChainChanged = () => {
      goToDashboard();
    };

    window.ethereum.on('accountsChanged', onAccountsChanged);
    window.ethereum.on('chainChanged', onChainChanged);

    return () => {
      window.ethereum.removeListener?.('accountsChanged', onAccountsChanged);
      window.ethereum.removeListener?.('chainChanged', onChainChanged);
    };
  }, [dispatch, userAccount, location.pathname, navigate]);

  return (
    <div className="root" style={{ backgroundColor: "#000", minHeight: "100vh" }}>
      <CssBaseline />
      <ResponsiveAppBar />
      <Toolbar />

      <Container>
        <br />

        {/* 👇 Banner shown for brand-new Xbox users without MSFS data */}
        {noMSFS && (
          <Box sx={{ p: 2, mb: 2, borderRadius: 1, backgroundColor: "#7c2d12", border: "1px solid #9a3412", color: "white", fontWeight: 600 }}>
            Game info not available for this Xbox ID (no Microsoft Flight Simulator data found).
          </Box>
        )}

        {/* Pilot NFT banner shows if connected wallet owns a Pilot NFT */}
        <PilotBanner walletAddress={userAccount?.walletAddress} />

        {userActivity?.loading && <MetaeroLoader />}

        {userInfo.result && userInfo.result.credits <= 3 && userInfo.result.credits >= 0 &&
          <Box sx={{ backgroundColor: "orange", color: "white" }}>Low credits</Box>}
        {userInfo.result && userInfo.result.credits < 0 &&
          <Box sx={{ backgroundColor: "red", color: "white" }}>Out of credits</Box>}

        <Ticker slideSpeed="40" isNewsTicker="true">
          {Array.isArray(userActivity.result) && !userActivity?.loading && userActivity.result.slice(1, 5).map((row, idx) => (
            <NewsTicker
              key={row.name || idx}
              id={row.name}
              title={<Typography variant="caption" sx={{ fontWeight: 700, color: 'red' }}>{new Date(row.alert_datetime).toUTCString()} - {row.message.replace(new RegExp(escapeRegExp("%0a"), 'g'), "\n")}</Typography>}
            />
          ))}
        </Ticker>

        {!userInfo.result &&
          <Box sx={{ mx: 2, opacity: 0.9, backgroundColor: "#111", color: "white", border: "2px solid #333" }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: "white" }}>
              {'FLY TO EARN - MSFS NEW ECONOMY'}
            </Typography>
          </Box>}

        <br />
        {!userInfo.result &&
          <Box sx={{ mx: 2, opacity: 0.8, backgroundColor: "#222", color: "white", border: "2px solid #333" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "white" }}>
              LOGIN AND MINT YOUR FLIGHT HOURS
            </Typography>
            <center>
              <br />
              <section>
                <div className="bsk-container">
                  <a href="https://api.xbl.io/app/auth/73ad1282-537a-49f8-ba1e-85b186fe94b6" onClick={() => { handleMSFSsignup(); }}>
                    <button
                      disabled={userInfo.loading}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
                        border: "2px solid #3b82f6",
                        padding: "12px 28px",
                        borderRadius: "12px",
                        cursor: "pointer",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
                        transition: "all 0.3s ease",
                        transform: userInfo.loading ? "scale(0.95)" : "scale(1)"
                      }}
                    >
                      <object type="image/svg+xml" data="https://app.metaero.org/xbox.svg" className="x-icon" style={{ width: "28px", height: "28px" }}></object>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "white", letterSpacing: "0.5px" }}>
                        {userInfo.loading ? "Connecting..." : "Sign in with Microsoft Xbox"}
                      </Typography>
                    </button>
                  </a>
                </div>
                <div>
                  <br/>
                  <img width="200px" src="https://dapp.metaero.org/FLY-min.png"></img>
                </div>
              </section>
              <br />
            </center>
          </Box>}

        <br />
        {userInfo.loading && <MetaeroLoader />}
        <br />

        <Grid container>
          <Grid item xs={12} sm={12}>
            <ErrorBoundaries>
              <Suspense fallback={<div>Loading...</div>}>
                <Routes>
                  <Route exact path={`/`} element={<Dashboard />} />
                  <Route path={`/dashboard`} element={<FlightSearch />} />
                  <Route path={`/my-alerts`} element={<MyAlerts />} />
                  <Route path={`/profile`} element={<Profile />} />
                  <Route path={`/terms`} element={<Terms />} />
                  <Route path={`/privacy`} element={<Privacy />} />
                  <Route path={`/billing`} element={<Billing />} />
                  <Route path={`/about-us`} element={<About />} />
                  <Route path={`/pricing`} element={<Pricing />} />
                  <Route path={`/activity-log`} element={<ActivityLog />} />
                </Routes>
              </Suspense>
            </ErrorBoundaries>

            <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: "#111", color: "white" }} elevation={6}>
              <Box>{/* bottom nav intentionally empty */}</Box>
              <br />
              <Typography variant="caption" sx={{ fontSize: 8, color: 'grey' }}>
                All rights Reserved. 2025 © www.metaero.org
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default Home;
